// ◉ xlVISION · Powering Business Together!
// © ExcelVision, Lda. — Braga, Portugal
// ( ·:·●●·:· ) xlVISION | Powering Business Together!
//
// ============================================================================
// Segmentos de uma linha — o que a barra contínua precisa de saber
// ============================================================================
//
// PORQUE EXISTE
//   O mapa deixou de repetir a sigla do cliente em cada dia. Um itinerário passa
//   a ser **uma barra** com o nome escrito uma vez, o que exige saber onde cada
//   corrida começa e acaba. Este ficheiro calcula isso, e mais nada.
//
//   É a única aritmética de índices do mapa que pode estar errada em silêncio: um
//   segmento deslocado um dia desenha o itinerário sobre o dia errado e a grelha
//   continua com bom aspecto. Isolada, testa-se.
//
// AS QUATRO NATUREZAS DE UM DIA — e porque não são uma só
//   `itinerario`  serviço normal. Agrupa-se com os dias vizinhos do MESMO
//                 itinerário: é o que forma a barra.
//   `estado`      viatura indisponível (oficina, avaria, sinistro). Agrupa-se
//                 pelo motivo, e não tem motorista — nesses dias o motorista
//                 está em casa ou noutra viatura (D-055).
//   `rotacao`     chegada E partida no mesmo dia. **NUNCA agrupa**: é sempre um
//                 dia isolado, de propósito, porque é o corte entre duas barras.
//                 É onde a margem do objectivo O1 se ganha, e tem de se ver.
//   `parado`      sem viagem, mas **com local**: onde a viatura fica. Foi o
//                 cliente a exigi-lo — «nos dias sem viagem tem de ser definido
//                 onde o motorista dorme». Medido na folha real: dos 2 402 dias
//                 sem corrida, só 240 (10 %) têm local registado. É lacuna da
//                 folha actual, não descrição dela.
//                 ⚠️ Agrupa-se pela LOCALIDADE, e não numa chave só. Porque estar
//                 na base e estar parado em Madrid são coisas diferentes: a
//                 primeira custa zero, a segunda é alojamento pago (D-056,
//                 D-060). Fundir as duas numa barra escondia exactamente a
//                 distinção que interessa ver.
//   vazio         sem serviço e sem local. Não gera segmento — e é o que fica a
//                 faltar preencher.
// ============================================================================

import { BANDEIRA, type CelulaCompacta } from '@xlv-gva-mpt/dominio';

export type TipoSegmento =
  | 'itinerario'
  | 'estado'
  | 'rotacao'
  | 'parado'
  | 'transito'
  /**
   * ⚠️⚠️ **Este dia e de OUTRO plano: ocupado, e nao avariado** (D-1).
   *
   * 📗 Cliente, 2026-08-21: *«aparece a ferramenta como se fosse oficina e diz "noutro
   * plano". Qual e a logica disto?»*
   *
   * 📌 Enquanto o bloco opaco emprestava o `estado_viatura`, este dia caia no `'estado'` e
   * desenhava a **chave inglesa** com a cor `grave`. Tem tipo proprio porque o que ele diz e
   * o oposto: *a viatura esta a trabalhar* — so nao e para mim.
   *
   * ⚠️ A chave e o `estado_viatura` (ou -1): oficina e avaria sao factos sobre a VIATURA e
   * atravessam o plano, logo dois dias com estados diferentes nao partilham barra.
   */
  | 'outroPlano';

export interface Segmento {
  readonly tipo: TipoSegmento;
  /** Primeiro dia visível do segmento, já recortado à janela. Inclusivo. */
  readonly dia0: number;
  /** Exclusivo. */
  readonly dia1: number;
  /**
   * `true` quando `dia0` é o verdadeiro início da corrida, e não um corte da
   * janela. É o que decide se a aresta da barra leva canto redondo e tampa, ou
   * se fica a cortar — sem isto, cada deslocamento faria aparecer inícios falsos.
   */
  readonly abre: boolean;
  readonly fecha: boolean;
  /** Índice do itinerário, ou do estado da viatura. -1 na rotação. */
  readonly chave: number;
  /** Índice do cliente. -1 quando não há. */
  readonly cliente: number;
  /**
   * Índice da CORRIDA — a viagem. -1 quando não há.
   *
   * É por aqui que o mapa liga a mesma viagem em **duas viaturas diferentes**:
   * medido na folha real, 43 % das referências de reserva aparecem em mais de um
   * autocarro, 46 delas em dias sobrepostos.
   */
  readonly corrida: number;
}

export interface SegmentoMotorista {
  readonly dia0: number;
  readonly dia1: number;
  readonly abre: boolean;
  readonly fecha: boolean;
  readonly motorista: number;
  /** 2.º condutor simultâneo — tripulação dupla, art. 8.º n.º 5. */
  readonly motorista2: number;
  /**
   * ⚠️ Começa a MEIO do dia `dia0`, e acaba a MEIO do dia `dia1 - 1`.
   *
   * 📗 Cliente, 2026-08-14: *«muitas vezes é no dia de rotação que se muda o motorista, tem
   * de estar metade da célula com um motorista e outra com outra»*.
   *
   * 📌 **Medido na folha real** (`horas-e-condutor-na-rotacao.mjs`): a troca acontece em
   * **50 de 293 rotações (17,1 %)** — o condutor da véspera não é o do dia seguinte. E a
   * folha lista dois nomes na célula em apenas **4 de 330 (1,2 %)**, porque tem **uma**
   * célula por viatura-dia: a informação existe na operação e perde-se no registo.
   *
   * ⚠️ **Não é tripulação dupla.** Duas pessoas em SEQUÊNCIA — uma fecha a viagem que chega,
   * a outra abre a que parte — não é o mesmo que duas ao mesmo tempo no mesmo autocarro
   * (art. 4.º o), 8.º n.º 5). É por isso que o condutor da partida vive no campo 14 da
   * célula e não no 6.
   */
  readonly inicioAMeio: boolean;
  readonly fimAMeio: boolean;
  /**
   * `true` quando este segmento é a viagem que PARTE num dia de rotação — o condutor saiu do
   * campo 14. Serve ao ecrã para dizer **qual** das duas viagens esta metade conduz.
   */
  readonly daPartida: boolean;
}

/**
 * A natureza de um dia, para efeitos de agrupamento.
 *
 * ⚠️ A chave do itinerário é a **CORRIDA** (índice 10) e não a assinatura de rota
 * (índice 5). A diferença é medida: na folha real 2 679 assinaturas servem 15 132
 * corridas — cada assinatura reutilizada ~6 vezes. Agrupar pela assinatura fundia
 * duas viagens distintas e seguidas com a mesma origem/destino numa só barra.
 *
 * Onde não há corrida identificada cai-se na assinatura, que é o melhor que há.
 */
export function natureza(c: CelulaCompacta | null): { tipo: TipoSegmento; chave: number } | null {
  if (c === null) return null;
  // ---- ⚠️⚠️ O OUTRO PLANO VEM PRIMEIRO, e a ordem e a decisao (D-1) --------------
  //
  //   Um dia de outro plano chega **sem cliente, sem itinerario e sem corrida** (o bloco opaco
  //   do ADR-002) e desde hoje **sem `estado_viatura` emprestado**. 📌 Sem este ramo caia no
  //   `'parado'` — e «parada fora» sobre um autocarro a trabalhar e o oposto da verdade, alem de
  //   inventar uma pernoita paga que ninguem paga.
  //
  // ⚠️ **Vem antes do `'estado'` de proposito.** Se o outro plano tiver a viatura em oficina,
  //   as duas coisas sao verdade ao mesmo tempo; a que explica ao planeador **porque nao pode
  //   usar este dia** e esta, e o estado vai na chave para o rotulo o poder dizer tambem.
  if ((c[4] & BANDEIRA.NOUTRO_PLANO) !== 0) return { tipo: 'outroPlano', chave: c[7] };
  if (c[7] >= 0) return { tipo: 'estado', chave: c[7] };
  if ((c[4] & BANDEIRA.ROTACAO) !== 0) return { tipo: 'rotacao', chave: -1 };
  // ---- ⚠️⚠️ EM TRÂNSITO ≠ PARADO, e a diferença é o oposto uma da outra ----
  //
  //   📗 Cliente, 2026-08-13: *«dias em vazio de viagem de volta à base têm que ter um registo
  //   em trânsito»*.
  //
  // ⚠️ **Antes caía em `parado`** — sem itinerário e sem corrida, a regra abaixo dizia «está
  //   parada num sítio». E dizia o **contrário do que acontece**: a viatura está a **andar**,
  //   com condutor e com horas de condução. 📌 A barra escrevia «parada fora» sobre um autocarro
  //   a atravessar Espanha.
  //
  // ⚠️ **Vem ANTES da regra do parado**, senão nunca é alcançada. E a chave é a **localidade**
  //   como no parado: dois trânsitos para sítios diferentes não partilham barra.
  if ((c[4] & BANDEIRA.EM_TRANSITO) !== 0) return { tipo: 'transito', chave: c[1] };
  // Sem itinerário e sem corrida: a viatura está parada num sítio — EXCEPTO se houver
  // cliente. 📗 Cliente, 2026-08-26: *«se não tiver itinerário é uma viagem à mesma… tem
  // de ser marcada de tal maneira»*. `alocacao.cliente_id` só é escrito pela RESERVA de um
  // pedido (nunca por uma paragem inferida) — é o sinal certo de que há serviço, mesmo sem
  // itinerário nem referência. Sem este ramo, um pedido criado sem itinerário (o "Novo
  // Pedido" nunca pede uma referência) aparecia "parada fora" em todos os dias, mesmo
  // reservado e com condutor.
  if (c[5] < 0 && c[10] < 0) {
    if (c[0] < 0) return { tipo: 'parado', chave: c[1] };
    // ⚠️ Chave PRÓPRIA, disjunta da corrida (≥0) e da assinatura de rota (≤-2, ver abaixo):
    //    sem corrida nem itinerário para agrupar, agrupa-se pelo CLIENTE.
    //
    // 🅿️ **Limitação aceite, e não corrigida aqui:** duas viagens diferentes do MESMO
    //    cliente, ambas sem itinerário, em dias seguidos na mesma viatura, fundem-se numa
    //    só barra — `CelulaCompacta` não leva o `pedido_id` (ver o documento de
    //    continuação). O cliente aceitou o compromisso.
    return { tipo: 'itinerario', chave: -(c[0] + 1_000_000) };
  }
  // ⚠️ ESPACOS DE CHAVE DISJUNTOS.
  //
  // A corrida e a rota sao duas tabelas diferentes, e ambas indexam de 0. Usar os
  // indices em cru fazia a corrida 0 e a rota 0 serem a MESMA chave — e o realce
  // por clique acendia barras sem relacao nenhuma. Apanhado por teste.
  //
  // A corrida fica positiva; a rota fica negativa a partir de -2, porque -1 esta
  // reservado a rotacao.
  return { tipo: 'itinerario', chave: c[10] >= 0 ? c[10] : -(c[5] + 2) };
}

/**
 * Segmentos da linha que se cruzam com `[janela0, janela1)`.
 *
 * Percorre a linha **inteira** e só depois recorta. É deliberado: para saber se
 * um segmento abre ou fecha dentro da janela é preciso conhecer os dias de fora,
 * e percorrer 365 posições por linha visível custa alguns milhares de iterações
 * por deslocamento — nada, ao lado do trabalho de layout.
 */
export function segmentos(
  linha: readonly (CelulaCompacta | null)[],
  janela0: number,
  janela1: number,
): Segmento[] {
  const saida: Segmento[] = [];
  let i = 0;
  while (i < linha.length) {
    const n = natureza(linha[i] ?? null);
    if (n === null) {
      i++;
      continue;
    }

    let fim = i + 1;
    // A rotação nunca agrupa: é sempre um dia. Ver o cabeçalho.
    if (n.tipo !== 'rotacao') {
      while (fim < linha.length) {
        const seg = natureza(linha[fim] ?? null);
        if (seg === null || seg.tipo !== n.tipo || seg.chave !== n.chave) break;
        fim++;
      }
    }

    const c0 = Math.max(i, janela0);
    const c1 = Math.min(fim, janela1);
    // `c1 > c0` e nao apenas a intersecao: com uma janela de largura zero — que
    // acontece no primeiro quadro, antes de o contentor ter tamanho — a
    // intersecao dava um segmento de 0 px de largura. Um segmento vazio nao e
    // «nada», e desenha-se.
    if (c1 > c0) {
      saida.push({
        tipo: n.tipo,
        dia0: c0,
        dia1: c1,
        abre: c0 === i,
        fecha: c1 === fim,
        chave: n.chave,
        cliente: linha[i]?.[0] ?? -1,
        corrida: linha[i]?.[10] ?? -1,
      });
    }

    i = fim;
  }
  return saida;
}

/**
 * Corridas de motorista da linha.
 *
 * Separadas das do itinerário porque **não coincidem**: o motorista pode trocar
 * a meio de um itinerário (o `trsf` da folha real, com local) e o mesmo motorista
 * pode atravessar dois itinerários seguidos. É o modelo operacional do negócio —
 * a viatura fica fora e os motoristas rodam — e desenhá-las como uma só coisa
 * esconderia exactamente aquilo que o planeador precisa de ver.
 *
 * Os dias em que a viatura está indisponível não entram: não têm motorista.
 */
export function segmentosMotorista(
  linha: readonly (CelulaCompacta | null)[],
  janela0: number,
  janela1: number,
): SegmentoMotorista[] {
  // ---- 1 · CADA DIA DÁ UM OU DOIS PEDAÇOS ---------------------------------
  //
  // ⚠️⚠️ **É AQUI QUE A TROCA NO DIA DE ROTAÇÃO PASSA A EXISTIR.** Antes agrupava-se por
  //   `motorista:motorista2` **por dia inteiro**, logo um dia com dois condutores em
  //   SEQUÊNCIA — um a fechar a viagem que chega, outro a abrir a que parte — só podia
  //   mostrar um deles: o campo 14 não entrava em conta nenhuma e o segundo condutor
  //   **não aparecia em lado nenhum**. 📌 Nada estourava e a célula ficava plausível.
  //
  // 📌 O dia passa a ser uma posição FRACCIONÁRIA: `d` a `d + 0,5` é a primeira metade,
  //   `d + 0,5` a `d + 1` a segunda. Um dia normal é um pedaço só, de `d` a `d + 1` — logo
  //   nada muda em 99 % das células, e há testes que o exigem.
  interface Pedaco {
    readonly ini: number;
    readonly fim: number;
    readonly motorista: number;
    readonly motorista2: number;
    readonly daPartida: boolean;
  }
  const pedacos: Pedaco[] = [];
  for (let d = 0; d < linha.length; d++) {
    const c = linha[d] ?? null;
    // Um dia indisponível não tem motorista, e um dia sem condutor registado também não.
    if (c === null || c[7] >= 0) continue;
    const mot = c[2];
    const motSaida = c[14];
    const trocaNoDia = motSaida >= 0 && motSaida !== mot;

    if (trocaNoDia) {
      // ⚠️ A primeira metade pode não ter condutor registado (`-1`): a viagem que chega pode
      //   estar «por atribuir» e a que parte já ter alguém. Nesse caso só há a segunda
      //   metade — e mostrar a célula inteira com o condutor da partida afirmava que ele
      //   conduziu o dia todo, incluindo a viagem de outra pessoa.
      if (mot >= 0) {
        pedacos.push({ ini: d, fim: d + 0.5, motorista: mot, motorista2: c[6], daPartida: false });
      }
      pedacos.push({
        ini: d + 0.5,
        fim: d + 1,
        motorista: motSaida,
        // ⚠️ A tripulação dupla DA VIAGEM QUE PARTE — campo 15, acrescentado a 2026-08-14.
        //   Antes ficava `-1` e o segundo condutor dela **não aparecia em lado nenhum**.
        motorista2: c[15] ?? -1,
        daPartida: true,
      });
      continue;
    }
    if (mot < 0) continue;
    pedacos.push({ ini: d, fim: d + 1, motorista: mot, motorista2: c[6], daPartida: false });
  }

  // ---- 2 · JUNTAR OS PEDAÇOS CONTÍGUOS DA MESMA PESSOA --------------------
  //
  // ⚠️ Contíguos **e** com a mesma tripulação: o fim de um tem de ser exactamente o início do
  //   seguinte. Sem essa condição, um condutor que fecha a viagem da manhã e volta a aparecer
  //   à tarde de outro dia era desenhado como uma barra contínua por cima do dia de outra
  //   pessoa.
  const corridas: Pedaco[] = [];
  for (const p of pedacos) {
    const ultimo = corridas[corridas.length - 1];
    if (
      ultimo !== undefined &&
      ultimo.fim === p.ini &&
      ultimo.motorista === p.motorista &&
      ultimo.motorista2 === p.motorista2
    ) {
      corridas[corridas.length - 1] = { ...ultimo, fim: p.fim };
      continue;
    }
    corridas.push(p);
  }

  // ---- 3 · CORTAR À JANELA e devolver em dias inteiros + as meias-marcas ---
  const saida: SegmentoMotorista[] = [];
  for (const p of corridas) {
    const ini = Math.max(p.ini, janela0);
    const fim = Math.min(p.fim, janela1);
    if (fim <= ini) continue;
    saida.push({
      dia0: Math.floor(ini),
      dia1: Math.ceil(fim),
      abre: ini === p.ini,
      fecha: fim === p.fim,
      motorista: p.motorista,
      motorista2: p.motorista2,
      // ⚠️ As meias-marcas são calculadas DEPOIS do corte: um segmento cortado pela janela
      //   começa num dia inteiro, e dizer que começa a meio punha meia coluna de folga no
      //   bordo do ecrã.
      inicioAMeio: ini % 1 !== 0,
      fimAMeio: fim % 1 !== 0,
      daPartida: p.daPartida,
    });
  }
  return saida;
}
